/**
 * Where a device name links to — one rule for every V2 table.
 *
 * `getDeviceProfileLink` (libs.jsx) is the rule for a device row: PalCare (4) and
 * Accutech wandering (6) always link, Inovonics (1) links except its system types
 * (network coordinator 10, repeater 11, 56, locator 57 -- their profile page renders
 * empty, checked live), and any other manufacturer (e.g. 10 SIP speakers, 7 Belle)
 * has no profile. For a device NAME, use components/v2/DeviceNameLink.jsx. Its
 * `/community/devices/{mid}/{id}` route reaches the same pages as the older
 * `/profiles/inovonics|palcare|accutech/{id}` routes (Community.jsx / Profiles.jsx).
 *
 * `getAlertDeviceLink` is the same rule for an ALERT row, plus the one alert that is
 * not a device alarm: onsite-server-offline (event type 41). Its device_id is the
 * server's palcare_device_id and a server also reports manufacture_id 4, so without
 * this check it would open a PalCare device profile for the wrong record.
 *
 * These were copied into ~15 tables and had drifted: some always linked to the
 * Inovonics profile, one sent every non-PalCare device to manufacturer 1, and only
 * the Alerts table knew about server-offline (docs/DUPLICATION_AUDIT.md, A15 / B8).
 */

import { getDeviceProfileLink, iAmHigherAuthority } from '../libs.jsx';
import { SERVER_OFFLINE_TYPE_ID } from './activeAlerts.js';

export { getDeviceProfileLink };

/* Below this the server-profile route has nothing useful to show a caregiver, so
   the device name renders as plain text instead of a dead end. */
export const SERVER_PROFILE_WEIGHT = 80;

/**
 * @param {object} row — an alert row: device_id, manufacture_id, device_type_id?, event_type_id
 * @param {number} roleWeight — the viewer's role weight
 * @returns {string|null}
 */
export function getAlertDeviceLink(row, roleWeight) {
  if (!row?.device_id) return null;
  if ((row.event_type_id ?? '').toString() === SERVER_OFFLINE_TYPE_ID) {
    return iAmHigherAuthority(SERVER_PROFILE_WEIGHT, roleWeight)
      ? `/community/server_devices/${row.device_id}`
      : null;
  }
  return getDeviceProfileLink(row);
}
